import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Container, Row, Col, Card, CardBody } from "reactstrap";
import GameMap from "../Maps/logic/GameMap";
import { mapConfigs } from "../Maps/MapConfig/mapConfigs";
import api from "../../api";
// Import our custom hook for Groudon rarity check
import useGroudonRarity from "../../Hooks/useGroudonRarity";

// CSS imports
import "./BattleTower.css";
import "./BattleTowerCapture.css";
import "../Maps/styles/CaptureAnimations.css";

// Pokeball images for selection UI
import pokeballImg from "../../assets/images/items/pokeball.png";
import greatballImg from "../../assets/images/items/greatball.png";
import ultraballImg from "../../assets/images/items/ultraball.png";
import rangeballImg from "../../assets/images/items/rangeball.png";
import longballImg from "../../assets/images/items/longball.png";

// Pokeball item IDs
const POKEBALL_ITEM_ID = 2;
const GREATBALL_ITEM_ID = 3;
const ULTRABALL_ITEM_ID = 4;
const LONGBALL_ITEM_ID = 4000;
const RANGEBALL_ITEM_ID = 4001;

export default function BattleTower() {
  const navigate = useNavigate();
  const [progress, setProgress] = useState(null);
  const [loading, setLoading] = useState(true);
  const [currentFloor, setCurrentFloor] = useState(1);
  const [towerType, setTowerType] = useState(null);
  
  // Timer state for 60-second challenge
  const [timeRemaining, setTimeRemaining] = useState(60);
  const [timerActive, setTimerActive] = useState(false);
  const [accessExpiration, setAccessExpiration] = useState(null);

  // Use our custom hook to fetch Groudon rarity data
  const { loading: rarityLoading, error: rarityError, showDiamond, groudonCount, diamondGroudonCount } = useGroudonRarity();

  // Log any errors with the Groudon rarity API
  useEffect(() => {
    if (rarityError) {
      console.error('Error fetching Groudon rarity:', rarityError);
    }
  }, [rarityError]);

  // State for inventory and pokeball selection
  const [inventory, setInventory] = useState([]);
  const [selectedBall, setSelectedBall] = useState(null);
  const [showPokeballSelection, setShowPokeballSelection] = useState(false);
  const [groudonData, setGroudonData] = useState(null); // Store Groudon data for capture

  // State for capture animation
  const [showCaptureAnimation, setShowCaptureAnimation] = useState(false);
  const [capturedPokemonImg, setCapturedPokemonImg] = useState(null);
  const [captureMessage, setCaptureMessage] = useState("");
  const [showPopup, setShowPopup] = useState(false);
  const [showStatusDetails, setShowStatusDetails] = useState(false);

  // Define fetchProgress first to avoid the "used before defined" ESLint warning
  const fetchProgress = useCallback(async () => {
    try {
      // Get tower type from URL path
      const path = window.location.pathname;
      console.log('Current path:', path);
      
      const currentTowerType = path.includes('60second') ? '60second' : 
                               path.includes('endless') ? 'endless' : 'original';
      console.log('Detected tower type:', currentTowerType);
      
      setTowerType(currentTowerType);
      
      // Debug log
      console.log(`Fetching progress for tower type: ${currentTowerType}`);
      
      const res = await api.get(`/v1/battle-tower/progress?tower_type=${currentTowerType}`, { withCredentials: true });
      console.log('API response:', res.data);
      
      const data = res.data.data || res.data;
      console.log('Raw progress data:', data);
      
      // Set progress data
      setProgress(data);
      
      // Handle current floor based on tower type and database values
      // For v2 tables, use current_floor field
      // For original tables, might be using different field names
      if (data.current_floor !== undefined) {
        console.log(`Setting current floor to ${data.current_floor} from API`);
        setCurrentFloor(data.current_floor || 1);
      } else if (data.current_floor_v2 !== undefined) {
        console.log(`Setting current floor to ${data.current_floor_v2} from API (v2)`);
        setCurrentFloor(data.current_floor_v2 || 1);
      } else {
        console.log('No current floor found in API response, defaulting to 1');
        setCurrentFloor(1);
      }
      
      // Handle timer for 60-second challenge
      if (currentTowerType === '60second') {
        console.log('60-second tower data:', data);
        
        // Check if we have active access - use the backend's has_active_access field
        const hasActiveAccess = data && data.has_active_access === true;
        
        if (hasActiveAccess) {
          console.log(`Setting timer from API: ${data.remaining_seconds} seconds remaining`);
          // Make sure we don't set a negative value and have a default
          const secondsRemaining = data.remaining_seconds !== undefined ? 
                                 Math.max(0, data.remaining_seconds) : 60;
          
          setTimeRemaining(secondsRemaining);
          setTimerActive(secondsRemaining > 0);
          setAccessExpiration(data.access_expiration);
          
          // Only redirect if time is explicitly 0
          if (data.remaining_seconds !== undefined && data.remaining_seconds <= 0) {
            console.log('Time already expired, redirecting to entrance');
            navigate('/map/BattleTowerEntrance', { 
              state: { 
                expired: true,
                message: 'Your 60-second challenge has ended!',
                towerType: '60second', 
                highestFloor: data.highest_floor || 1
              }
            });
          }
        } else {
          console.log('No active access detected');
          // Redirect to entrance if no active access
          navigate('/map/BattleTowerEntrance', { 
            state: { 
              expired: true,
              message: 'You need to purchase access to the 60-second challenge',
              towerType: '60second'
            }
          });
        }
      }
    } catch (error) {
      console.error('Error fetching battle tower progress:', error);
      // Set default values to prevent blank screen
      setCurrentFloor(1);
      if (window.location.pathname.includes('60second')) {
        setTowerType('60second');
        // Don't set a default time - redirect to entrance instead
        navigate('/map/BattleTowerEntrance', { 
          state: { 
            error: true,
            message: 'Error loading tower data. Please try again.',
            towerType: '60second'
          }
        });
      } else {
        setTowerType('endless');
      }
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // Handle Groudon capture - wrapped in useCallback to avoid dependency issues
  const captureGroudon = useCallback(async () => {
    try {
      // Start capture animation
      setShowCaptureAnimation(true);

      // Add earthquake shake effect
      const shakeCSS = `
        @keyframes groudonCaptureShake {
          0% { transform: translateX(0px) translateY(0px); }
          25% { transform: translateX(-8px) translateY(-6px); }
          50% { transform: translateX(6px) translateY(-8px); }
          75% { transform: translateX(-6px) translateY(4px); }
          100% { transform: translateX(0px) translateY(0px); }
        }
        .groudon-capture-shake {
          animation: groudonCaptureShake 80ms ease-in-out infinite !important;
        }
      `;

      const styleElement = document.createElement('style');
      styleElement.id = 'groudon-capture-shake-styles';
      styleElement.textContent = shakeCSS;
      document.head.appendChild(styleElement);

      // Apply shake to body
      document.body.classList.add('groudon-capture-shake');

      // Call the backend endpoint to capture Groudon
      const response = await api.post("/v1/battle-tower/catch-groudon", {}, { withCredentials: true });

      // Remove shake effect after 2 seconds
      setTimeout(() => {
        document.body.classList.remove('groudon-capture-shake');
        const shakeStyle = document.getElementById('groudon-capture-shake-styles');
        if (shakeStyle) shakeStyle.remove();
      }, 2000);

      if (response.data.success) {
        // Set the correct sprite based on the captured Groudon type
        const isVariant = response.data.groudon_type === "diamond";
        const spriteUrl = isVariant ? "/Pokemon/DiamondGroudon.png" : "/Pokemon/GroudonOverworld.png";
        setCapturedPokemonImg(spriteUrl);

        // Use message from response
        const message = response.data.message;
        setCaptureMessage(message);

        // Update progress after successful capture
        await fetchProgress();

        // Force re-render of the map to remove Groudon NPC
        // This works because the map config checks progress.groudon_captured
        setCurrentFloor(prev => prev); // Re-render with same floor
      }
    } catch (error) {
      console.error("Error capturing Groudon:", error);
      setCaptureMessage(error.response?.data?.detail || "Failed to capture Groudon");
      setShowCaptureAnimation(false);
    }

    // Hide capture animation after 8 seconds to give users time to read the message
    setTimeout(() => {
      setShowCaptureAnimation(false);
      setCapturedPokemonImg(null);
      setCaptureMessage("");
    }, 8000);
  }, [fetchProgress]);

  // Handle NPC interactions (for battles with grunts/Maxie/Groudon)
  const handleNPCClick = useCallback((npc) => {
    if (npc.type === "grunt" || npc.type === "trainer") {
      // Start battle with trainer/grunt on the current floor
      const opponent = npc.type === "trainer" ? "grunt" : npc.type;
      navigate(`/battle?opponent=${opponent}&floor=${currentFloor}&tower_type=${towerType}`);
    } else if (npc.type === "maxie") {
      // Start battle with Maxie
      navigate(`/battle?opponent=maxie&floor=100&tower_type=${towerType}`);
    } else if (npc.type === "groudon") {
      // Start Groudon capture sequence
      captureGroudon();
    }
  }, [navigate, currentFloor, captureGroudon, towerType]);

  // Fetch battle tower progress on component mount
  useEffect(() => {
    fetchProgress();
  }, [fetchProgress]);

  // Initialize timer when component mounts for 60-second challenge
  // This is now handled in fetchProgress to ensure we use the server time
  // We don't need this effect anymore as we're getting the time from the API

  // 60-second timer logic
  useEffect(() => {
    let timer;
    console.log('Timer effect running, active:', timerActive, 'remaining:', timeRemaining, 'tower type:', towerType);
    
    if (timerActive && timeRemaining > 0 && towerType === '60second') {
      console.log('Starting timer interval');
      timer = setInterval(() => {
        setTimeRemaining(prevTime => {
          const newTime = prevTime - 1;
          console.log('Timer tick:', newTime);
          if (newTime <= 0) {
            console.log('Timer expired, will call timeout endpoint');
            clearInterval(timer);
            return 0;
          }
          return newTime;
        });
      }, 1000);
    }
    
    return () => {
      if (timer) {
        console.log('Clearing timer');
        clearInterval(timer);
      }
    };
  }, [timerActive, timeRemaining, towerType, navigate, progress, currentFloor]);
  
  // Force redirect if timer is 0
  useEffect(() => {
    if (timeRemaining === 0 && towerType === '60second') {
      console.log('Time expired, calling timeout endpoint');
      
      // Call timeout endpoint to reset progress and record highest floor
      const handleTimeout = async () => {
        try {
          const response = await api.post('/v1/battle-tower/timeout', 
            { tower_type: '60second' }, 
            { withCredentials: true }
          );
          
          console.log('Timeout response:', response.data);
          
          // Refresh progress to get updated data from database
          await fetchProgress();
          
          navigate('/map/BattleTowerEntrance', { 
            state: { 
              expired: true,
              message: response.data.message || 'Your 60-second challenge has ended!',
              towerType: '60second', 
              finalFloor: response.data.final_floor,
              highestFloor: response.data.highest_floor
            }
          });
        } catch (error) {
          console.error('Error calling timeout endpoint:', error);
          // Fallback to basic redirect
          navigate('/map/BattleTowerEntrance', { 
            state: { 
              expired: true,
              message: 'Your 60-second challenge has ended!',
              towerType: '60second', 
              highestFloor: progress?.highest_floor || currentFloor 
            }
          });
        }
      };
      
      handleTimeout();
    }
  }, [timeRemaining, towerType, navigate, progress, currentFloor, fetchProgress]);

  // Handle battle victory (called from URL params or battle completion)
  const handleBattleVictory = useCallback(async (floor, boss) => {
    try {
      // Try to log the victory for the completed floor with tower type
      const victoryData = towerType ? { tower_type: towerType } : {};
      await api.post(`/v1/battle-tower/victory/${floor}`, victoryData, { withCredentials: true });

      if (boss === 'maxie') {
        console.log('Maxie defeated, moving to Groudon chamber');
        setCurrentFloor(101); // Move to Groudon chamber immediately
      } else {
        console.log(`Moving to floor ${floor + 1}`);
        setCurrentFloor(floor + 1); // Move to next floor immediately
      }

      // Update progress
      fetchProgress();
    } catch (error) {
      console.error('Error handling battle victory:', error);

      // If the victory was already processed, just advance to the next floor
      if (error.response?.data?.detail === "Battle Tower floor victory already processed") {
        console.log(`Floor ${floor} victory already processed, advancing to next floor`);

        if (boss === 'maxie') {
          setCurrentFloor(101); // Move to Groudon chamber
        } else {
          setCurrentFloor(floor + 1); // Move to next floor
        }

        // Update progress to get the latest state
        fetchProgress();
      } else {

      }
    }
  }, [fetchProgress, towerType]);

  // Handle battle loss (called from URL params or battle completion)
  const handleBattleLoss = useCallback(async (floor) => {
    try {
      const lossData = towerType ? { tower_type: towerType } : {};
      const response = await api.post(`/v1/battle-tower/loss/${floor}`, lossData, { withCredentials: true });

      // Reset to checkpoint floor
      setTimeout(() => {
        setCurrentFloor(response.data.checkpoint_floor || 1);
      }, 3000);

      // Update progress
      fetchProgress();
    } catch (error) {
      console.error('Error handling battle loss:', error);
    } finally {
      setLoading(false);
    }
  }, [fetchProgress, towerType]);

  // Check URL params for battle results
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const battleResult = urlParams.get('battle_result');
    const floor = urlParams.get('floor');
    const boss = urlParams.get('boss');

    if (battleResult === 'victory' && floor) {
      handleBattleVictory(parseInt(floor), boss);
      // Clean up URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    } else if (battleResult === 'loss' && floor) {
      handleBattleLoss(parseInt(floor));
      // Clean up URL params
      window.history.replaceState({}, document.title, window.location.pathname);
    }
  }, [handleBattleVictory, handleBattleLoss]);

  // For testing - force floor 101 to see Groudon
  useEffect(() => {
    // Uncomment this line to force floor 101 for testing
    // setCurrentFloor(101);
    console.log(`Current floor: ${currentFloor}`);
  }, []);

  // Get the appropriate map config for current floor
  const getMapConfig = () => {
    // Check tower type first
    if (towerType === '60second') {
      // 60-second tower uses minute tower maps with Battle Tower trainers
      if (currentFloor === 1) {
        return mapConfigs.battleMinuteTower1;
      } else if (currentFloor % 2 === 0) {
        return mapConfigs.battleMinuteTower2;
      } else {
        return mapConfigs.battleMinuteTower3;
      }
    } else if (towerType === 'endless') {
      // Endless tower uses infinite tower maps
      if (currentFloor === 1) {
        return mapConfigs.battleinfiniteTower1;
      } else if (currentFloor % 2 === 0) {
        return mapConfigs.battleinfiniteTower2;
      } else {
        return mapConfigs.battleinfiniteTower3;
      }
    } else {
      // Original tower logic (fallback)
      if (currentFloor === 1) {
        return mapConfigs.battleTower1;
      } else if (currentFloor === 100) {
        return mapConfigs.battleTower100;
      } else if (currentFloor === 101) {
        // Special case: Groudon chamber after defeating Maxie
        const groudonMapConfig = {
          ...mapConfigs.battleTowerGroudon,
          npcs: [
            {
              id: "groudon",
              type: "groudon",
              x: 240,
              y: 240,
              sprite: showDiamond ? "/Pokemon/DiamondGroudon.png" : "/Pokemon/GroudonOverworld.png",
              width: 64,
              height: 64,
              clickable: true,
              message: "Groudon is watching you..."
            }
          ]
        };
        return groudonMapConfig;
      } else if (currentFloor % 2 === 0) {
        return mapConfigs.battleTower2;
      } else {
        return mapConfigs.battleTower3;
      }
    }
  };

  // Format time remaining as MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="battle-tower-loading">
        <div className="loading-spinner"></div>
        <p>Loading Battle Tower...</p>
      </div>
    );
  }

  // Create custom map config with NPC click handler
  const getCustomMapConfig = () => {
    console.log('Getting custom map config for tower type:', towerType, 'floor:', currentFloor);
    const baseConfig = getMapConfig();
    console.log('Base map config:', baseConfig);

    // If no map config is available, use a fallback
    if (!baseConfig) {
      console.warn('No map config found, using fallback');
      return mapConfigs.battleTower1;
    }

    // Process NPCs based on type
    let modifiedNpcs = baseConfig.npcs || [];

    // First, filter out Groudon if already captured
    if (progress && progress.groudon_captured) {
      modifiedNpcs = modifiedNpcs.filter(npc => npc.type !== "groudon");
      console.log("Removed Groudon from map - already captured");
    }

    // Then process remaining NPCs
    modifiedNpcs = modifiedNpcs.map(npc => {
      // Remove dialogue property from grunt NPCs to ensure battle trigger works
      if (npc.type === "grunt") {
        // Create a new object without the dialogue property
        const { dialogue, ...npcWithoutDialogue } = npc;
        return npcWithoutDialogue;
      }

      // Handle battle tower specific NPC types
      if (npc.type === "minute" || npc.type === "endless") {
        // Remove dialogue property from battle tower NPCs to ensure battle trigger works
        const { dialogue, ...npcWithoutDialogue } = npc;
        return npcWithoutDialogue;
      }

      // Handle Groudon sprite based on capture count
      if (npc.type === "groudon" && progress) {
        // Check if we're on the Groudon floor and have progress data
        const totalCaptured = progress.total_groudon_captured || 0;

        // NOTE: We no longer override the sprite here
        // The sprite is now set dynamically in getMapConfig() based on the useGroudonRarity hook
        console.log(`Groudon variant check: Total captured: ${totalCaptured}, Using API-based sprite selection`);

        // Return the NPC without modifying the sprite
        return npc;
      }

      return npc;
    });

    // Create a unique key that changes when the floor changes
    // This forces GameMap to re-mount and reset player position to initialPlayer
    const mapKey = `battle-tower-floor-${currentFloor}`;

    // Add steam points for visual effect (similar to Ember Spa)
    const steamPoints = [
      { x: 100, y: 120 },
      { x: 180, y: 150 },
      { x: 260, y: 120 },
      { x: 140, y: 200 },
      { x: 220, y: 200 }
    ];

    return {
      ...baseConfig,
      npcs: modifiedNpcs,
      floor: currentFloor, // Pass the current floor number to GameMap
      onNPCClick: handleNPCClick,
      onBattleVictory: handleBattleVictory, // Pass the battle victory handler to GameMap
      onBattleLoss: handleBattleLoss, // Pass the battle loss handler to GameMap
      key: mapKey, // Add a key that changes with floor to force re-mount
      steamPoints: steamPoints, // Add steam points for visual effect
      showSteamEffects: true // Enable steam effects for Battle Tower
    };
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Row>
            <Col xs="12">
              <Card>
                <CardBody className="p-0">
                  <div className="battle-tower-container">
                  
                    
                    {/* Timer display for 60-second challenge */}
                    {towerType === '60second' && (
                      <div className="timer-container">
                        <div className={`timer ${timeRemaining <= 10 ? 'timer-warning' : ''}`}>
                          {formatTime(timeRemaining)}
                        </div>
                      </div>
                    )}
                    
                    <div className="game-section">
                      <GameMap 
                        key={`battle-tower-floor-${currentFloor}`}
                        config={getCustomMapConfig()} 
                      />

                      {/* Tower Status Section - Only show for endless tower */}
                      {progress && towerType !== '60second' && (
                        <div className="magma-tower-status">
                          <div 
                            className="status-header" 
                            onClick={() => setShowStatusDetails(!showStatusDetails)}
                          >
                            <div className="status-header-content">
                              <h5>Tower Status</h5>
                              <div className="status-summary">
                                <div className="floor-badge">Floor {currentFloor}</div>
                                <div className="coin-badge">
                                  <span>{progress.highest_floor || 1}</span>
                                </div>
                              </div>
                              <span className="collapse-icon">
                                {showStatusDetails ? '▲' : '▼'}
                              </span>
                            </div>
                          </div>
                          
                          {showStatusDetails && (
                            <div className="status-content">
                              <ul className="list-unstyled mb-0">
                                <li className="d-flex justify-content-between py-2">
                                  <span>Current Floor</span>
                                  <span className="status-value">{currentFloor}</span>
                                </li>
                                <li className="d-flex justify-content-between py-2">
                                  <span>Highest Floor</span>
                                  <span className="status-value">{progress.highest_floor || 1}</span>
                                </li>
                              </ul>
                            </div>
                          )}
                        </div>
                      )}
                      
                      {captureMessage && (
                        <div className="capture-message">{captureMessage}</div>
                      )}
                    </div>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>
    </React.Fragment>
  );
}